/**
 * CommercialCriteriaTable — renders Gate 2 commercial criteria approval UI.
 *
 * Displays the markdown prompt, weight summary, a criteria table with [i]
 * description popovers + scoring-type chips, and a confirm button.
 *
 * Author: Raj Kumar (raj.kumar@accellor.com)
 */
import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  IconButton,
  Popover,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import MarkdownRenderer from "../../MarkdownRenderer";

type RankDefinition = {
  label: string;
  rank: number;
};

type CommercialCriterion = {
  criterion_id: string;
  criterion_name: string;
  description: string;
  weight: number;
  category?: string;
  scoring_type?: "relative_price" | "relative_rank" | "absolute";
  rank_definitions?: RankDefinition[];
  is_active?: boolean;
};

type CommercialCriteriaTableProps = {
  criteria: CommercialCriterion[];
  commercialWeight: number;
  technicalWeight: number;
  message: string;
  isSubmitting: boolean;
  hasApproved: boolean;
  onEditCriterion: (index: number) => void;
  onConfirm: () => void;
  totalPercentage: number;
  isWeightageBalanced: boolean;
  header?: string;
};

const SCORING_TYPE_LABELS: Record<string, string> = {
  relative_price: "Price Comparison",
  relative_rank: "Ranked",
  absolute: "Absolute",
};

export default function CommercialCriteriaTable({
  criteria,
  commercialWeight,
  technicalWeight,
  message,
  isSubmitting,
  hasApproved,
  onEditCriterion,
  onConfirm,
  totalPercentage,
  isWeightageBalanced,
  header,
}: CommercialCriteriaTableProps): JSX.Element {
  const hasAnyScoringType = criteria.some((c) => c.scoring_type);

  const [criterionDescPopover, setCriterionDescPopover] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  const handleOpenDesc = (key: string, e: React.MouseEvent<HTMLElement>) => {
    setCriterionDescPopover((prev) => ({ ...prev, [key]: e.currentTarget }));
  };

  const handleCloseDesc = (key: string) => {
    setCriterionDescPopover((prev) => ({ ...prev, [key]: null }));
  };

  return (
    <>
      {/* Weight summary / header */}
      {header ? (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Typography variant="h5" sx={{ color: "#004141", fontWeight: 600, mb: 0 }}>
            {header}
          </Typography>
        </Paper>
      ) : (technicalWeight > 0 || commercialWeight > 0) ? (
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <Chip
            label={`Technical: ${technicalWeight}%`}
            sx={{
              backgroundColor: "#004141",
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "13px",
            }}
          />
          <Chip
            label={`Commercial: ${commercialWeight}%`}
            sx={{
              backgroundColor: "#00a888",
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "13px",
            }}
          />
        </Box>
      ) : null}

      {/* Criteria table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mb: 3,
          border: "1px solid #b2e5de",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0fbf9" }}>
              <TableCell
                sx={{ fontWeight: 600, color: "#004141", fontSize: "14px", borderBottom: "2px solid #b2e5de" }}
              >
                Criteria
              </TableCell>
              {hasAnyScoringType && (
                <TableCell
                  sx={{ fontWeight: 600, color: "#004141", fontSize: "14px", borderBottom: "2px solid #b2e5de", textAlign: "center" }}
                >
                  Scoring Type
                </TableCell>
              )}
              <TableCell
                sx={{ fontWeight: 600, color: "#004141", fontSize: "14px", borderBottom: "2px solid #b2e5de", textAlign: "center" }}
              >
                Percentage Weightage
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, color: "#004141", fontSize: "14px", borderBottom: "2px solid #b2e5de", textAlign: "center", width: "80px" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {criteria.map((criterion, index) => {
              const popoverKey = `comm_criterion_${index}`;
              return (
                <TableRow
                  key={criterion.criterion_id}
                  sx={{
                    "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                    "&:hover": { backgroundColor: "#f0fbf9" },
                  }}
                >
                  {/* Criteria name + [i] popover */}
                  <TableCell sx={{ fontSize: "14px", color: "#2a312b" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {criterion.criterion_name}
                      {criterion.description && (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenDesc(popoverKey, e)}
                            sx={{ padding: "4px", color: "#1976d2", "&:hover": { backgroundColor: "#e3f2fd" } }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <Popover
                            open={Boolean(criterionDescPopover[popoverKey])}
                            anchorEl={criterionDescPopover[popoverKey]}
                            onClose={() => handleCloseDesc(popoverKey)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            transformOrigin={{ vertical: "top", horizontal: "left" }}
                          >
                            <Paper sx={{ p: 2, maxWidth: 420 }}>
                              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#004141", mb: 1 }}>
                                {criterion.criterion_name}
                              </Typography>
                              <Typography sx={{ fontSize: "13px", color: "#2a312b", mb: criterion.scoring_type ? 1 : 0 }}>
                                {criterion.description}
                              </Typography>
                              {criterion.scoring_type && (
                                <Chip
                                  label={SCORING_TYPE_LABELS[criterion.scoring_type] ?? criterion.scoring_type}
                                  size="small"
                                  sx={{ mt: 0.5, backgroundColor: "#f0fbf9", color: "#004141", fontWeight: 600, fontSize: "11px" }}
                                />
                              )}
                              {criterion.rank_definitions && criterion.rank_definitions.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#004141", mb: 0.5 }}>
                                    Rank Definitions
                                  </Typography>
                                  {criterion.rank_definitions.map((rd) => (
                                    <Typography key={rd.label} sx={{ fontSize: "12px", color: "#2a312b" }}>
                                      Rank {rd.rank}: {rd.label}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Paper>
                          </Popover>
                        </>
                      )}
                    </Box>
                  </TableCell>

                  {/* Scoring type chip — hidden for technical-only criteria */}
                  {hasAnyScoringType && (
                    <TableCell sx={{ textAlign: "center" }}>
                      <Chip
                        label={
                          criterion.scoring_type
                            ? (SCORING_TYPE_LABELS[criterion.scoring_type] ?? criterion.scoring_type)
                            : "—"
                        }
                        size="small"
                        sx={{ backgroundColor: "#f0fbf9", color: "#004141", fontWeight: 600, fontSize: "11px", border: "1px solid #b2e5de" }}
                      />
                    </TableCell>
                  )}

                  {/* Weight chip */}
                  <TableCell sx={{ fontSize: "14px", color: "#2a312b", textAlign: "center" }}>
                    <Chip
                      label={`${(criterion.weight || 0).toFixed(0)}%`}
                      sx={{ backgroundColor: "#004141", color: "#ffffff", fontWeight: 600, fontSize: "12px", height: "26px" }}
                    />
                  </TableCell>

                  {/* Edit button */}
                  <TableCell sx={{ textAlign: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => onEditCriterion(index)}
                      disabled={isSubmitting || hasApproved}
                      sx={{ color: "#004141", "&:hover": { backgroundColor: "#f0fbf9" } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ backgroundColor: "#f0fbf9" }}>
              <TableCell
                colSpan={hasAnyScoringType ? 2 : 1}
                sx={{ fontWeight: 700, color: "#004141", fontSize: "14px", borderTop: "2px solid #b2e5de", textAlign: "right", paddingRight: 3 }}
              >
                Total Percentage
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, color: "#004141", fontSize: "14px", borderTop: "2px solid #b2e5de", textAlign: "center" }}
              >
                <Chip
                  label={`${totalPercentage.toFixed(0)}%`}
                  sx={{
                    backgroundColor: isWeightageBalanced ? "#4caf50" : "#f44336",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "13px",
                    height: "28px",
                  }}
                />
              </TableCell>
              <TableCell sx={{ borderTop: "2px solid #b2e5de" }} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      {/* Markdown prompt — shown below the table */}
      {message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              "& h1": { fontSize: "20px", fontWeight: 600, color: "#004141", mb: 2 },
              "& h2": { fontSize: "16px", fontWeight: 600, color: "#004141", mb: 1.5, mt: 2 },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
              "& hr": { border: "none", borderTop: "1px solid #b2e5de", margin: "16px 0" },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Confirm button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Tooltip
          title={!isWeightageBalanced ? "Please balance the weightage to equal exactly 100%" : ""}
          arrow
        >
          <span>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={isSubmitting || hasApproved || !isWeightageBalanced}
              sx={{
                backgroundColor: "#00a888",
                "&:hover": { backgroundColor: "#008f75" },
                "&.Mui-disabled": { backgroundColor: "#ccc", color: "#666" },
                textTransform: "none",
                fontWeight: 600,
                px: 4,
              }}
            >
              {isSubmitting ? "Confirming..." : "Confirm and Continue"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </>
  );
}
